import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import {
  CityMoneyRoute, cityMoneyPages, cityMoneySlug, statewideMoneyPages,
} from '@/data/seo/moneyPages';
import {
  stateLabel, statePath, licenseNote, citySlug, cityNameFromSlug, stateCityPath,
  CITY_PAGES_PER_STATE, CITY_MONEY_PAGES_PER_STATE,
} from '@/data/seo/states';
import {
  breadcrumbSchema, faqSchema, serviceSchema, moneyFaqs, offerCatalogSchema, cityEntitySchema, speakableSchema, Faq,
} from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, EntityFacts, PageWrap, TrustStrip,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money, PLAN_INCLUDED_LEADS } from '@/data/constants';
import LiveProjects from '@/components/seo/LiveProjects';
import MembershipOptions from '@/components/seo/MembershipOptions';
import { StateAlertForm } from '@/components/seo/StatePageBlocks';
import { fetchCityIndex, CityCount } from '@/lib/stateAlerts';

// ---------------------------------------------------------------------------
// PER-TRADE CITY LEAD PAGE (outside California)
//   /en/leads/flooring-leads-orlando-fl
//
// Modeled on the California metro money pages. The city set is the top
// CITY_MONEY_PAGES_PER_STATE of the state from the browse-listings `cityIndex`
// aggregate (the SAME list the sitemap emits). Live projects are filtered by
// state + EXACT city + trade server-side. A city outside the current top list
// still renders (old links never 404) but is marked noindex.
// ---------------------------------------------------------------------------

const CityMoneyPage: React.FC<{ route: CityMoneyRoute }> = ({ route }) => {
  const { trade, st } = route;
  const { lang, path, t } = useI18n();
  const es = lang === 'es';
  // undefined = loading, null = aggregate unavailable.
  const [ranked, setRanked] = useState<CityCount[] | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    setRanked(undefined);
    fetchCityIndex(CITY_PAGES_PER_STATE).then((idx) => {
      if (alive) setRanked(idx ? idx[st.code] || [] : null);
    });
    return () => {
      alive = false;
    };
  }, [st.code]);

  const top = (ranked || []).slice(0, CITY_MONEY_PAGES_PER_STATE);
  const match = top.find((c) => citySlug(c.city) === route.citySlug);
  const known = match || (ranked || []).find((c) => citySlug(c.city) === route.citySlug);
  const city = known?.city || cityNameFromSlug(route.citySlug);
  // Index ONLY while the city is in the state's current top list (= sitemap set).
  const indexable = ranked !== undefined && ranked !== null && !!match;

  const tradeName = es ? trade.es : trade.en;
  const stateName = stateLabel(st, lang);
  const place = `${city}, ${st.code}`;
  const url = path(`/leads/${cityMoneySlug(trade.slug, city, st)}`);
  const h1 = es ? `Leads de ${tradeName} en ${place}` : `${trade.en} Leads in ${place}`;
  const marketplaceLink = `${path('/jobs')}?state=${st.code}&city=${encodeURIComponent(city)}&trade=${trade.marketplaceTrade}`;

  const answer = es
    ? `Los leads de ${tradeName.toLowerCase()} en ${place} son proyectos reales publicados por propietarios y contratistas generales de ${city}, ${stateName}. Cada lead cuesta ${money(LEAD_PRICE)} e incluye alcance y presupuesto; al desbloquearlo verá la dirección exacta, el código postal, los contactos aprobados y los planos adjuntos.`
    : `${trade.en} leads in ${place} are real projects posted by homeowners and general contractors in ${city}, ${stateName}. Each lead costs a flat ${money(LEAD_PRICE)} and shows the scope and budget range — unlocking it reveals the exact address, ZIP, approved contact details and any attached plan documents.`;

  const faqs: Faq[] = [
    ...moneyFaqs(trade.en, place, lang),
    {
      q: es ? `¿Necesito licencia para trabajos de ${tradeName.toLowerCase()} en ${city}?` : `Do I need a license for ${trade.en.toLowerCase()} work in ${city}?`,
      a: `${licenseNote(st, lang)} ${es ? `${city} puede exigir además registro municipal o permisos.` : `${city} may also require local registration or permits.`}`,
    },
    ...(known
      ? [{
          q: es ? `¿Cuántos proyectos de construcción hay abiertos en ${city}?` : `How many construction projects are open in ${city}?`,
          a: es
            ? `${known.count} ${known.count === 1 ? 'proyecto abierto' : 'proyectos abiertos'} en todos los oficios en ${place}, según el conteo en vivo de GIGGONE. Los proyectos de ${tradeName.toLowerCase()} aparecen abajo.`
            : `${known.count} open ${known.count === 1 ? 'project' : 'projects'} across all trades in ${place}, from GIGGONE's live count. ${trade.en} projects are listed below.`,
        }]
      : []),
  ];

  const label = (tr: { en: string; es: string }, where: string) => (es ? `Leads de ${tr.es} en ${where}` : `${tr.en} Leads ${where}`);
  const statewide = statewideMoneyPages(st.code);
  const statewideThisTrade = statewide.find((m) => m.trade.slug === trade.slug);

  const sameTradeOtherCities = [
    ...top
      .filter((c) => citySlug(c.city) !== route.citySlug)
      .map((c) => ({ label: label(trade, `${c.city}, ${st.code}`), to: path(`/leads/${cityMoneySlug(trade.slug, c.city, st)}`) })),
    ...(statewideThisTrade ? [{ label: label(trade, stateName), to: path(`/leads/${statewideThisTrade.slug}`) }] : []),
  ];
  const otherTradesHere = cityMoneyPages(st, city)
    .filter((m) => m.trade.slug !== trade.slug)
    .map((m) => ({ label: label(m.trade, place), to: path(`/leads/${m.slug}`) }));
  const locationLinks = [
    { label: es ? `Proyectos de construcción en ${place}` : `${place} construction projects`, to: path(stateCityPath(st, city)) },
    { label: es ? `Proyectos de ${tradeName.toLowerCase()} abiertos en ${city}` : `Open ${trade.en.toLowerCase()} projects in ${city}`, to: marketplaceLink },
    { label: es ? `Proyectos de construcción en ${st.es}` : `${st.en} construction projects`, to: path(statePath(st)) },
    { label: es ? `Resumen del oficio: ${tradeName}` : `${trade.en} trade overview`, to: path(`/trades/${trade.slug}`) },
    { label: es ? 'Todos los leads de construcción' : 'All construction leads', to: path('/leads') },
  ];

  return (
    <>
      <Seo
        title={h1}
        description={
          es
            ? `Compre leads de ${tradeName.toLowerCase()} en ${place} por ${money(LEAD_PRICE)}. Proyectos reales de ${city}, precio fijo, sin comisiones ocultas.`
            : `Buy ${trade.en.toLowerCase()} leads in ${place} for a flat ${money(LEAD_PRICE)}. Real ${city} projects, one price, no hidden bidding fees.`
        }
        noindex={!indexable}
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: 'Construction Leads', url: `/${lang}/leads` },
            { name: st.en, url: path(statePath(st)) },
            { name: city, url: path(stateCityPath(st, city)) },
            { name: h1, url },
          ]),
          serviceSchema({ trade: `${trade.en} leads`, area: `${city}, ${st.en}`, description: answer, url }),
          cityEntitySchema(city, st),
          offerCatalogSchema(),
          faqSchema(faqs),
          speakableSchema(url, h1),
        ]}
      />

      <PageHero eyebrow={t('seo.moneyEyebrow')} title={h1} intro={answer}>
        <Link to={path('/register')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t('seo.ctaSubBtn')}
        </Link>
        <Link to={marketplaceLink} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {es ? `Ver proyectos en ${city}` : `Browse ${city} projects`}
        </Link>
      </PageHero>

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: t('seo.leadsHub'), url: path('/leads') },
            { name: stateName, url: path(statePath(st)) },
            { name: city, url: path(stateCityPath(st, city)) },
            { name: h1 },
          ]}
        />

        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />

        <EntityFacts
          rows={[
            { k: t('seo.factTrade'), v: tradeName },
            { k: t('seo.factArea'), v: place },
            { k: es ? 'Proyectos abiertos (todos los oficios)' : 'Open projects (all trades)', v: ranked === undefined ? '…' : String(known?.count ?? 0) },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
            { k: t('seo.factIncluded'), v: `${PLAN_INCLUDED_LEADS.basic}–${PLAN_INCLUDED_LEADS.premium}` },
          ]}
        />

        <Section title={t('seo.whyBuy', { trade: tradeName, place })}>
          <p>{answer}</p>
          <p className="mt-3">{licenseNote(st, lang)}</p>
          <TrustStrip />
        </Section>

        <MembershipOptions />

        {/* state + EXACT city + trade, server-side (browse-listings cityExact). */}
        <LiveProjects
          state={st.code}
          city={city}
          trade={trade.marketplaceTrade}
          heading={es ? `Proyectos de ${tradeName.toLowerCase()} abiertos en ${city}` : `Open ${trade.en.toLowerCase()} projects in ${city}`}
        />

        <StateAlertForm st={st} city={city} />

        <FaqSection faqs={faqs} />

        <LinkGrid title={t('seo.sameTradeOther')} links={sameTradeOtherCities} icon="map" />
        <LinkGrid title={t('seo.otherTradesPlace', { place })} links={otherTradesHere} icon="trade" />
        <LinkGrid title={t('seo.relatedPages')} links={locationLinks} columns={2} />

        <CtaBand />
      </PageWrap>
    </>
  );
};

export default CityMoneyPage;
